/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM } from "@adapptio/lib-components";
import { IInputBaseProps, InputAdornment, InputBase } from "../../core";
import { IFieldValidationError } from "../FieldTypes";
import { FieldStatus } from "../FieldStatus";

import "./FieldInput.scss";
import { isNil, isUndefined } from "@adapptio/lib-shared";

export interface IFieldInputProps extends IInputBaseProps {
	id?: string;
	value?: string | number;
	className?: string;
	placeholder?: string;
	readOnly?: boolean;
	disabled?: boolean;
	errors?: IFieldValidationError[];
	minLength?: number;
	maxLength?: number;
	min?: number;
	max?: number;
	step?: string;
	inputRef?: React.MutableRefObject<HTMLInputElement>;
	autoFocus?: boolean;
	autoFocusSelect?: boolean;
	type?: React.HTMLProps<HTMLInputElement>["type"];
	inputMode?: React.HTMLProps<HTMLInputElement>["inputMode"];
	debounceTimeout?: number;
	multiLine?: boolean;
}

const FieldInput: React.FC<IFieldInputProps> = ({
	id,
	value,
	placeholder,
	readOnly,
	disabled,
	errors,
	className,
	onBlur,
	onChange,
	inputRef,
	minLength,
	maxLength,
	autoFocus,
	autoFocusSelect,
	type,
	inputMode,
	debounceTimeout,
	multiLine,
	maxRows,
	startAdornment,
	endAdornment,
	...props
}) => {
	const baseClass = BEM("edt-field-input");

	if (className) {
		baseClass.add(className);
	}

	const extraProps: Partial<IInputBaseProps> = {
		...props
	};

	if (!isUndefined(value)) {
		extraProps.value = isNil(value) ? "" : `${value}`;
	}

	return (
		<InputBase
			id={id}
			className={baseClass.toClassName()}
			baseClassName={"edt-field-input"}
			type={type}
			inputMode={inputMode}
			placeholder={placeholder}
			onChange={onChange}
			onBlur={onBlur}
			inputRef={inputRef}
			minLength={minLength}
			maxLength={maxLength}
			autoFocus={autoFocus}
			autoFocusSelect={autoFocusSelect}
			readOnly={readOnly}
			disabled={disabled}
			debounceTimeout={debounceTimeout}
			multiLine={multiLine}
			maxRows={maxRows}
			startAdornment={startAdornment}
			endAdornment={
				<>
					{errors?.length > 0 && (
						<InputAdornment>
							<FieldStatus errors={errors} />
						</InputAdornment>
					)}
					{endAdornment}
				</>
			}
			{...extraProps}
		/>
	);
};

const FieldInputMemo = React.memo(FieldInput);

export { FieldInputMemo as FieldInput };
